import React from 'react';
import { FORMAT_FIELD_SCHEMAS, MODAL_STYLES } from '../constants';

interface AdvancedOptionsPanelProps {
  format: string;
  useOverrides: boolean;
  overrides: Record<string, unknown>;
  onToggleUseOverrides: (v: boolean) => void;
  onOverrideChange: (key: string, value: unknown) => void;
  profileOptions?: Record<string, unknown> | null | undefined;
}

export default function AdvancedOptionsPanel({
  format,
  useOverrides,
  overrides,
  onToggleUseOverrides,
  onOverrideChange,
  profileOptions
}: AdvancedOptionsPanelProps) {
  const fields = FORMAT_FIELD_SCHEMAS[format] || [];
  const disabled = !useOverrides;

  const getValue = (key: string) => {
    const v = overrides?.[key];
    if (typeof v !== 'undefined') return v as any;
    const p = profileOptions?.[key];
    return typeof p !== 'undefined' ? (p as any) : '';
  };

  return (
    <div className="mt-2 space-y-4 bg-navy-700/20 rounded-lg p-4">
      <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={!!useOverrides}
          onChange={(e) => onToggleUseOverrides(e.target.checked)}
          className="w-5 h-5 rounded border-navy-600 bg-navy-700 text-yellow-400 focus:ring-2 focus:ring-yellow-400/50 focus:ring-offset-2 focus:ring-offset-navy-800 cursor-pointer transition-colors"
        />
        <span className="font-medium">Editar opções do formato ({String(format).toUpperCase()})</span>
      </label>

      <div className={`grid grid-cols-2 gap-4 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        {fields.map((f) => (
          <div key={f.key} className={f.type === 'boolean' ? 'flex items-center' : 'space-y-2'}>
            {f.type === 'boolean' ? (
              <label className="flex items-center gap-3 cursor-pointer w-full">
                <input
                  type="checkbox"
                  checked={Boolean(getValue(f.key))}
                  onChange={(e) => onOverrideChange(f.key, e.target.checked)}
                  className="w-5 h-5 rounded border-navy-600 bg-navy-700 text-yellow-400 focus:ring-2 focus:ring-yellow-400/50 focus:ring-offset-2 focus:ring-offset-navy-800 cursor-pointer transition-colors flex-shrink-0"
                />
                <span className="text-sm font-medium text-gray-300">{f.label}</span>
              </label>
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-300">{f.label}</label>
                {f.type === 'number' && (
                  <input
                    className="w-full min-w-[120px] px-4 py-2.5 text-sm rounded-lg bg-navy-700 text-gray-200 border border-navy-600 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400 transition-all no-spinner"
                    type="number"
                    min={f.min as any}
                    max={f.max as any}
                    step={f.step as any}
                    value={Number(getValue(f.key) ?? (typeof f.min === 'number' ? f.min : 0))}
                    onChange={(e) => onOverrideChange(f.key, Number(e.target.value))}
                    placeholder={String(f.min || 0)}
                  />
                )}
                {f.type === 'string' && (
                  <input
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-navy-700 text-gray-200 border border-navy-600 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400 transition-all"
                    type="text"
                    value={String(getValue(f.key) ?? '')}
                    onChange={(e) => onOverrideChange(f.key, e.target.value)}
                    placeholder={f.label}
                  />
                )}
                {f.type === 'select' && (
                  <select
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-navy-700 text-gray-200 border border-navy-600 hover:border-navy-500 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400 transition-all cursor-pointer"
                    value={String(getValue(f.key) ?? (f.options?.[0]?.value || ''))}
                    onChange={(e) => onOverrideChange(f.key, e.target.value)}
                  >
                    {(f.options || []).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </>
            )}
            {f.note && (
              <p className="text-[11px] text-gray-500">{f.note}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


